import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Button } from 'ui-components/components/button';
import { BulkErrorsAndWarningsService } from '../../../../services/bulk-errors-and-warnings-service';
import get from 'lodash.get';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { testId } from 'ui-components/services/testid-service';
import style from './bulk-error-or-warning-data-table.module.less';
import { emptyArray } from 'ui-components/default-props/empty';

const messages = defineMessages({
  orderNumber: {
    description: 'bulkErrorOrWarningDataTable.orderNumber',
    defaultMessage: 'Order #',
    id: 'Q2ATzN',
  },
  error: {
    description: 'bulkErrorOrWarningDataTable.error',
    defaultMessage: 'Error',
    id: '00MMg7',
  },
  warning: {
    description: 'bulkErrorOrWarningDataTable.warning',
    defaultMessage: 'Warning',
    id: 'HVaIrO',
  },
  remove: {
    description: 'bulkErrorOrWarningDataTable.remove',
    defaultMessage: 'Remove',
    id: '1h3J4n',
  },
  shipmentNumber: {
    description: 'bulkErrorOrWarningDataTable.shipmentNumber',
    defaultMessage: 'Shipment #',
    id: 'eXvXyC',
  },
});

const getOrderNumberColumn = (onOrderNumberClick) => {
  return {
    id: 'orderNumber',
    translation: messages.orderNumber,
    renderColumn: (data) => (
      <Button onClick={() => onOrderNumberClick(data)} testId='order-number-link' theme='link'>
        {data.salesOrderNumber}
      </Button>
    ),
  };
};

const getTooltippedColumnText = (text) => {
  return (
    <Tooltip position='bottom'>
      <TooltipContent>
        <div>{text}</div>
      </TooltipContent>
      <div className={style['column-body']}>{text}</div>
    </Tooltip>
  );
};

const getErrorColumn = () => {
  return {
    id: 'error',
    translation: messages.error,
    renderColumn: (data) => {
      const message = get(data, 'message', '');
      const handler = get(data, 'error.handler');
      const text = get(data, 'error.text', message);
      if (!handler) {
        return <span {...testId('error-col')}>{getTooltippedColumnText(text)}</span>;
      }
      return (
        <Button onClick={() => handler(data)} testId='error-col' theme='link'>
          {getTooltippedColumnText(text)}
        </Button>
      );
    },
  };
};

const getWarningColumn = () => {
  return {
    id: 'warning',
    translation: messages.warning,
    renderColumn: (data) => {
      const status = get(data, 'status', '');
      const handler = get(data, 'warning.handler');
      const text = get(data, 'warning.text', status);
      if (!handler) {
        return <span {...testId('warning-col')}>{getTooltippedColumnText(text)}</span>;
      }
      return (
        <Button onClick={() => handler(data)} testId='warning-col' theme='link'>
          {getTooltippedColumnText(text)}
        </Button>
      );
    },
  };
};

const getRemoveColumn = (removeItemFromSelection) => {
  return {
    id: 'remove',
    headerIsRightAligned: true,
    translation: messages.remove,
    renderColumn: (data) => (
      <div className={style['remove-button-wrapper']}>
        <Button
          className={style['remove-button']}
          onClick={() => removeItemFromSelection(data)}
          testId='remove-item-button'
          theme='unstyled'
        >
          <FontAwesomeIcon icon={faXmark} />
        </Button>
      </div>
    ),
  };
};

const getShipmentNumberColumn = (onShipmentNumberClick, disabled = false) => {
  return {
    id: 'shipmentNumber',
    translation: messages.shipmentNumber,
    renderColumn: (data) => (
      <Button disabled={disabled} onClick={() => onShipmentNumberClick(data)} testId='shipment-number-link' theme='link'>
        {data.fulfillmentPlanId}
      </Button>
    ),
  };
};

const getColumnsFromDataType = (dataType, isError, removeItemFromSelection, onOrderNumberClick, onShipmentNumberClick, isRemovable) => {
  if (dataType === 'salesOrder') {
    const columns = [getOrderNumberColumn(onOrderNumberClick), isError ? getErrorColumn() : getWarningColumn()];
    if (isRemovable) {
      columns.push(getRemoveColumn(removeItemFromSelection));
    }
    return columns;
  }
  if (dataType === 'fulfillmentPlan') {
    const columns = [
      getOrderNumberColumn(onOrderNumberClick),
      getShipmentNumberColumn(onShipmentNumberClick),
      isError ? getErrorColumn() : getWarningColumn(),
    ];
    if (isRemovable) {
      columns.push(getRemoveColumn(removeItemFromSelection));
    }
    return columns;
  }
  if (dataType === 'fulfillmentPlanWithoutSalesOrder') {
    const columns = [getShipmentNumberColumn(onShipmentNumberClick), isError ? getErrorColumn() : getWarningColumn()];
    if (isRemovable) {
      columns.push(getRemoveColumn(removeItemFromSelection));
    }
    return columns;
  }
  if (dataType === 'batchedFulfillmentPlan') {
    const columns = [getShipmentNumberColumn(null, true), isError ? getErrorColumn() : getWarningColumn()];
    if (isRemovable) {
      columns.push(getRemoveColumn(removeItemFromSelection));
    }
    return columns;
  }
  return [];
};

const BulkErrorOrWarningDataTableComponent = ({
  dataType,
  data = emptyArray,
  removeItemFromSelection,
  onOrderNumberClick,
  onShipmentNumberClick,
  className = '',
  isRemovable = true,
}) => {
  const isErrorType = data.some((errOrWarn) => get(errOrWarn, 'type') === BulkErrorsAndWarningsService.getBatchValidatedErrorType());
  const columns = getColumnsFromDataType(
    dataType,
    isErrorType,
    removeItemFromSelection,
    onOrderNumberClick,
    onShipmentNumberClick,
    isRemovable,
  );
  const topLevelClassName = classnames(
    style['bulk-error-or-warning-data-table'],
    {
      [style['two-columns']]: columns.length === 2,
      [style['three-columns']]: columns.length === 3,
      [style['four-columns']]: columns.length === 4,
    },
    className,
  );
  return (
    <div className={topLevelClassName}>
      <div className={style['header-row']}>
        {columns.map(({ headerIsRightAligned, translation, id }) => (
          <div
            key={id}
            className={classnames(style['header-cell'], {
              [style['right-align']]: headerIsRightAligned,
            })}
          >
            <FormattedMessage {...translation} />
          </div>
        ))}
      </div>
      <div className={style['body-rows']}>
        {data.map((datum) => (
          <div key={datum.id} className={style['body-row']}>
            {columns.map(({ id, renderColumn }) => (
              <div key={id} className={style['body-cell']}>
                {renderColumn(datum)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

BulkErrorOrWarningDataTableComponent.propTypes = {
  dataType: PropTypes.oneOf(['salesOrder', 'fulfillmentPlan', 'fulfillmentPlanWithoutSalesOrder', 'batchedFulfillmentPlan']).isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      salesOrderId: PropTypes.string,
      salesOrderNumber: PropTypes.string,
      fulfillmentPlanId: PropTypes.string,
      error: PropTypes.shape({
        text: PropTypes.string,
        handler: PropTypes.func,
      }),
    }),
  ),
  removeItemFromSelection: PropTypes.func.isRequired,
  onOrderNumberClick: PropTypes.func.isRequired,
  onShipmentNumberClick: PropTypes.func.isRequired,
  className: PropTypes.string,
  isRemovable: PropTypes.bool,
};

export const BulkErrorOrWarningDataTable = memo(BulkErrorOrWarningDataTableComponent);
